// src/FavoritesContext.js
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

const FavoritesContext = createContext();
export const useFavorites = () => useContext(FavoritesContext);

const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME (შენთან შეთანხმებული ფერები) ================= */
const COLORS = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  shadow: "0 20px 60px rgba(0,0,0,.18)",
};

/* ================= MODAL ================= */
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(17,24,39,.5);
  backdrop-filter: blur(1.5px);
  display: grid; place-items: center;
  z-index: 3000;
`;
const Modal = styled.div`
  width: min(420px, 92vw);
  background: ${COLORS.bg};
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  box-shadow: ${COLORS.shadow};
  overflow: hidden;
`;
const Header = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${COLORS.line};
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;
const Title = styled.h3`
  margin: 0; font-size: 16px; font-weight: 900; color: ${COLORS.text};
`;
const CloseBtn = styled.button`
  width: 34px; height: 34px;
  border-radius: 10px; border: 1px solid ${COLORS.line};
  background: #fff; display: grid; place-items: center; cursor: pointer;
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 30px rgba(0,0,0,.08); }
`;
const Body = styled.div`
  padding: 14px 16px; color: ${COLORS.text}; font-size: 14px;
`;
const Actions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  padding: 0 16px 16px;
`;
const Btn = styled.a`
  flex: 1 1 140px; height: 40px;
  border-radius: 10px; border: 1px solid ${COLORS.line};
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 14px; text-decoration: none; cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  background: #fff; color: ${COLORS.text};
  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(0,0,0,.08); }
`;
const BtnPrimary = styled(Btn)`
  background: ${COLORS.text}; color: #fff; border-color: ${COLORS.text};
`;

/* ================= PROVIDER ================= */
export const FavoritesProvider = ({ children }) => {
  const { language } = useLanguage();

  const [userId, setUserId] = useState(null);
  const [favProducts, setFavProducts] = useState(() => new Set());
  const [favGifts, setFavGifts] = useState(() => new Set());
  const pending = useRef(new Set());

  // 🔔 ავტორიზაციის მოთხოვნის მოდალი
  const [showAuthModal, setShowAuthModal] = useState(false);

  // lock scroll როცა მოდალი ღიაა
  useEffect(() => {
    if (showAuthModal) {
      const b = document.body;
      const prev = { overflow: b.style.overflow };
      b.style.overflow = "hidden";
      return () => { b.style.overflow = prev.overflow; };
    }
  }, [showAuthModal]);

  useEffect(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser?.id) {
        setUserId(storedUser.id);
        fetchFavorites(storedUser.id);
      }
    } catch {}
    // თუ სხვა ტაბში შევა, სინქი:
    const onStorage = () => {
      try {
        const u = JSON.parse(localStorage.getItem("user"));
        setUserId(u?.id || null);
        if (u?.id) fetchFavorites(u.id);
        else {
          setFavProducts(new Set());
          setFavGifts(new Set());
        }
      } catch {}
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const fetchFavorites = async (uid) => {
    try {
      const res = await api.get(`${API_BASE_URL}/get_favorites.php?user_id=${uid}`);
      if (res?.data?.success) {
        const items = res.data.favorites || [];
        const p = new Set();
        const g = new Set();
        for (const f of items) {
          if (f.type === "gift_set" || f.kind === "gift_set" || f.gift_set_id) {
            if (f.gift_set_id) g.add(String(f.gift_set_id));
          } else if (f.product_id) {
            p.add(String(f.product_id));
          }
        }
        setFavProducts(p);
        setFavGifts(g);
      }
    } catch (err) {
      console.warn("get_favorites failed:", err?.response?.data?.error || err.message);
    }
  };

  const hasFavorites = useMemo(() => favProducts.size + favGifts.size > 0, [favProducts, favGifts]);

  const isFavorite = (kind = "product", id) => {
    const key = String(id);
    return kind === "gift_set" ? favGifts.has(key) : favProducts.has(key);
  };

  const seedFavoriteFromInitial = (kind = "product", id, initialIsFavorite) => {
    if (!id || !initialIsFavorite) return;
    const key = String(id);
    if (kind === "gift_set") {
      setFavGifts((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    } else {
      setFavProducts((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    }
  };

  const addFavorite = async (kind = "product", id) => {
    const key = `${kind}:${String(id)}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);

    // optimistic UI
    if (kind === "gift_set") {
      setFavGifts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
    } else {
      setFavProducts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
    }

    try {
      const payload =
        kind === "gift_set"
          ? { user_id: userId, kind: "gift_set", gift_set_id: id }
          : { user_id: userId, kind: "product", product_id: id };

      const res = await api.post(`${API_BASE_URL}/add_to_favorites.php`, payload, { validateStatus: () => true });
      if (!res?.data?.success) {
        // revert + alert
        if (kind === "gift_set") {
          setFavGifts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
        } else {
          setFavProducts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
        }
        const msg = language === "ka" ? "ვერ დაემატა რჩეულებში" : "Failed to add to favorites";
        alert(res?.data?.error || res?.data?.message || msg);
      }
    } catch (err) {
      if (kind === "gift_set") {
        setFavGifts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
      } else {
        setFavProducts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
      }
      const msg = language === "ka" ? "ქსელის შეცდომა" : "Network error";
      alert(err?.response?.data?.error || err.message || msg);
    } finally {
      pending.current.delete(key);
    }
  };

  const removeFavorite = async (kind = "product", id) => {
    const key = `${kind}:${String(id)}`;
    if (pending.current.has(key)) return;
    pending.current.add(key);

    // optimistic UI
    if (kind === "gift_set") {
      setFavGifts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
    } else {
      setFavProducts((prev) => { const n = new Set(prev); n.delete(String(id)); return n; });
    }

    try {
      const payload =
        kind === "gift_set"
          ? { user_id: userId, kind: "gift_set", gift_set_id: id }
          : { user_id: userId, kind: "product", product_id: id };

      const res = await api.post(`${API_BASE_URL}/remove_from_favorites.php`, payload, { validateStatus: () => true });
      if (!res?.data?.success) {
        // revert + alert
        if (kind === "gift_set") {
          setFavGifts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
        } else {
          setFavProducts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
        }
        const msg = language === "ka" ? "ვერ წაიშალა რჩეულებიდან" : "Failed to remove from favorites";
        alert(res?.data?.error || res?.data?.message || msg);
      }
    } catch (err) {
      if (kind === "gift_set") {
        setFavGifts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
      } else {
        setFavProducts((prev) => { const n = new Set(prev); n.add(String(id)); return n; });
      }
      const msg = language === "ka" ? "ქსელის შეცდომა" : "Network error";
      alert(err?.response?.data?.error || err.message || msg);
    } finally {
      pending.current.delete(key);
    }
  };

  const toggleFavorite = (kind = "product", id) => {
    // ❗ თუ არ არის ავტორიზებული — აჩვენე მოდალი
    if (!userId) {
      setShowAuthModal(true);
    } else {
      return isFavorite(kind, id) ? removeFavorite(kind, id) : addFavorite(kind, id);
    }
  };

  const refreshFavorites = () => { if (userId) fetchFavorites(userId); };

  // i18n ტექსტები მოდალისთვის
  const txt = {
    title: language === "ka" ? "ავტორიზაციაა საჭირო" : "Authorization required",
    desc:
      language === "ka"
        ? "რჩეულებში დასამატებლად გთხოვთ გაიაროთ ავტორიზაცია."
        : "Please log in to add items to your favorites.",
    login: language === "ka" ? "შესვლა" : "Log in",
    close: language === "ka" ? "დახურვა" : "Close",
  };

  return (
    <FavoritesContext.Provider
      value={{
        hasFavorites,
        refreshFavorites,

        isFavorite,
        toggleFavorite,
        addFavorite,
        removeFavorite,
        seedFavoriteFromInitial,
      }}
    >
      {children}

      {/* 🔔 Auth Required Modal */}
      {showAuthModal && (
        <Overlay onClick={() => setShowAuthModal(false)} role="dialog" aria-modal="true">
          <Modal onClick={(e) => e.stopPropagation()}>
            <Header>
              <Title>{txt.title}</Title>
              <CloseBtn onClick={() => setShowAuthModal(false)} aria-label="Close">×</CloseBtn>
            </Header>
            <Body>{txt.desc}</Body>
            <Actions>
              <BtnPrimary href="/login">{txt.login}</BtnPrimary>
              <Btn as="button" onClick={() => setShowAuthModal(false)}>{txt.close}</Btn>
            </Actions>
          </Modal>
        </Overlay>
      )}
    </FavoritesContext.Provider>
  );
};
